import {inject, Injectable} from '@angular/core';
import {ToastService} from '../services/toast.service';
import {HttpResponse} from "@angular/common/http";
import {finalize, map, Observable, shareReplay, tap} from "rxjs";
import {environment} from "../../../environments/environment";
import {OptionsConfig} from "../models/types/options-config.model";
import {OptionsResponse} from "../models/types/options-response.model";
import {PhoneCountry} from "../models/categories/phone-country.model";
import {Response} from "../models/response.model";
import {EntityListQuery} from "../models/entities/entity-list-state.model";
import {Entity} from "../models/entities/entity/entity.model";
import {DEFAULT_COLUMN_WIDTH, TABLE__DEFAULT_PER_PAGE} from "../../util/constants";
import {Category} from "../models/categories/category.model";
import {ScreenStateResponse} from "../models/categories/screen-state-response.model";
import {ListEntity} from "../models/entities/entity/entity-list.model";
import {EntityDocuments} from "../models/categories/entity-documents.model";
import {BaseApiService} from "./base-api.service";
import {InlineEditResult} from "../models/entities/entity/inline-edit-result.model";
import {RepeaterRecordLookup} from '../models/entities/field/field-related/props.model';
import {BulkActivityEmailSaveService} from '../../dashboard/components/agent-email-preview/bulk-activity-email-save.service';

export interface ContactReviewResult {
  contacts: import('../../shared/components/contact-review/contact-review.component').ContactCard[];
  selected_contact?: import('../../shared/components/contact-review/contact-review.component').SelectedContactProfile | null;
  requires_review: boolean; can_confirm: boolean; has_more: boolean; review_token: string | null; phone_conflict?: boolean;
  next_page_token?: string | null; has_hidden_matches?: boolean; reviewed_count?: number;
}

@Injectable({
  providedIn: 'root'
})
export class EntityService extends BaseApiService {
  private readonly bulkEmailSave = inject(BulkActivityEmailSaveService);
  private readonly bulkSaveToast = inject(ToastService);
  private static readonly listSortOptions = new Set(['asc', 'desc']);

  /**
   * One in-flight (or just completed) request per (entity, id, field set) — e.g. many app-form / repeater
   * rows each run applyRules and would otherwise duplicate the same record-attributes call.
   */
  private recordAttributesShared = new Map<string, Observable<Response<Record<string, unknown>>>>();

  /**
   * `field/.../operations` and `field/.../search` validate `related_entity.id` as integer when present.
   * Invalid route ids (`+\"foo\"` → NaN) must not be sent — JSON turns NaN into null and Laravel returns 422.
   */
  private static relatedEntityBody(
    related_entity: OptionsConfig['related_entity']
  ): { name?: string; id?: number } {
    const body: { name?: string; id?: number } = {};
    if (related_entity.name) {
      body.name = related_entity.name;
    }
    const raw = related_entity.id;
    if (raw == null) {
      return body;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isInteger(n) && n >= 1) {
      body.id = n;
    }
    return body;
  }

  public getCreate(
    entity: string,
    formType: string,
    relatedEntity?: { id: number; name: string } | null
  ): Observable<Response<Entity>> {
    const hasRelatedEntity = relatedEntity?.name && relatedEntity.id != null && relatedEntity.id > 0;

    return this.http.post<Response<Entity>>(environment.baseApi + 'entity/create', {
      entity,
      ...(hasRelatedEntity && {
        related_entity: {
          id: relatedEntity!.id,
          name: relatedEntity!.name,
        },
      }),
      data: {
        form_type: formType,
        include: [
          "relations"
        ]
      }
    });
  }

  public storeEntity(data: FormData): Observable<Response<Partial<Entity>>> {
    return this.http.post<Response<Partial<Entity>>>(environment.baseApi + 'entity/store', data);
  }

  public reviewContact(entity: string, values: Record<string, unknown>, recordId: number | null, pageToken?: string): Observable<Response<ContactReviewResult>> {
    return this.http.post<Response<ContactReviewResult>>(environment.baseApi + 'entity/search', {
      entity, related_entity: 'contact_persons', data: {field: 'name', page: 1,
        contact_review: true, contact_values: values, ...(recordId ? {record_id: recordId} : {}),
        ...(pageToken ? {contact_review_page_token: pageToken} : {})}
    });
  }

  public update(data: FormData): Observable<Response<InlineEditResult>> {
    return this.http.post<Response<InlineEditResult>>(environment.baseApi + 'entity/update', data);
  }

  public getEntityList(state: EntityListQuery): Observable<Response<ListEntity>> {
    return this.http
      .post<Response<ListEntity>>(environment.baseApi + 'entity/list', this.buildListRequestBody(state))
      .pipe(map(res => ({
        ...res,
        data: this.fillDefaultDimension(res.data)
      })))
  }

  public getListIds(state: EntityListQuery): Observable<Response<{ ids: number[]; revisions: Record<number, number> }>> {
    return this.http.post<Response<{ ids: number[]; revisions: Record<number, number> }>>(
      environment.baseApi + 'entity/list-ids',
      this.buildListRequestBody(state)
    );
  }

  public exportListToExcel(
    state: EntityListQuery,
    recordsIds: number[]
  ): Observable<Response<{ file: string; extension: string; name: string }>> {
    return this.http.post<Response<{ file: string; extension: string; name: string }>>(
      environment.baseApi + 'entity/list-export',
      {
        ...this.buildListRequestBody(state),
        records_ids: recordsIds,
      }
    );
  }

  private buildListRequestBody(state: EntityListQuery): Record<string, unknown> {
    return {
      ...(state.related_entity && {related_entity: state.related_entity}),
      entity: state.entity,
      operations: EntityService.sanitizeListOperations(state.operations),
      fields: EntityService.sanitizeListFields(state.fields),
      ...(state.use_state !== undefined && { use_state: state.use_state }),
      data: {
        page: state.pagination.page,
        per_page: state.pagination.per_page,
        text: state.pagination.search_term ?? ''
      },
    };
  }

  private fillDefaultDimension(data: ListEntity): ListEntity {
    return {
      ...data,
      fields: data.fields.map(field => ({
        ...field,
        dimensions: field.dimensions ? field.dimensions : {width: (field.dimensions ?? DEFAULT_COLUMN_WIDTH)}
      }))
    }
  }

  private static sanitizeListOperations(operations: unknown): Array<{
    id: number;
    sort: 'asc' | 'desc' | null;
    except: unknown[];
    options: unknown[];
    terms: unknown[];
    excluded_terms: unknown[];
    selected_all?: boolean;
    replace_all?: boolean;
  }> {
    if (!Array.isArray(operations)) {
      return [];
    }

    return operations
      .map(operation => EntityService.sanitizeListOperation(operation))
      .filter((operation): operation is NonNullable<typeof operation> => operation !== null);
  }

  private static sanitizeListOperation(operation: unknown): {
    id: number;
    sort: 'asc' | 'desc' | null;
    except: unknown[];
    options: unknown[];
    terms: unknown[];
    excluded_terms: unknown[];
    selected_all?: boolean;
    replace_all?: boolean;
  } | null {
    if (!EntityService.isRecord(operation)) {
      return null;
    }

    const id = EntityService.toInteger(operation['id']);
    if (id === null) {
      return null;
    }

    const rawSort = operation['sort'];
    const sort = typeof rawSort === 'string' && EntityService.listSortOptions.has(rawSort)
      ? rawSort as 'asc' | 'desc'
      : null;
    const selectedAll = typeof operation['selected_all'] === 'boolean'
      ? operation['selected_all']
      : undefined;
    const replaceAll = typeof operation['replace_all'] === 'boolean'
      ? operation['replace_all']
      : undefined;

    return {
      id,
      sort,
      except: EntityService.toArray(operation['except']),
      options: EntityService.toArray(operation['options']),
      terms: EntityService.toArray(operation['terms']),
      excluded_terms: EntityService.toArray(operation['excluded_terms']),
      ...(selectedAll !== undefined && { selected_all: selectedAll }),
      ...(replaceAll !== undefined && { replace_all: replaceAll }),
    };
  }

  private static sanitizeListFields(fields: unknown): Array<{
    id: number;
    position: number;
    dimensions: Record<string, unknown>;
  }> {
    if (!Array.isArray(fields)) {
      return [];
    }

    return fields
      .map(field => EntityService.sanitizeListField(field))
      .filter((field): field is NonNullable<typeof field> => field !== null);
  }

  private static sanitizeListField(field: unknown): {
    id: number;
    position: number;
    dimensions: Record<string, unknown>;
  } | null {
    if (!EntityService.isRecord(field)) {
      return null;
    }

    const id = EntityService.toInteger(field['id']);
    const position = EntityService.toInteger(field['position']);
    const dimensions = EntityService.isRecord(field['dimensions']) ? field['dimensions'] : null;

    if (id === null || position === null || dimensions === null) {
      return null;
    }

    return {
      id,
      position,
      dimensions,
    };
  }

  private static toArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private static toInteger(value: unknown): number | null {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isInteger(n) ? n : null;
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  public getEdit(entity: string, id: number, form_type: string, cloneDate?: string, reassignmentTarget?: number): Observable<Response<Entity>> {
    return this.http.post<Response<Entity>>(environment.baseApi + 'entity/edit', {
      entity,
      data: {
        form_type,
        ...(entity === 'requests' && cloneDate ? {clone_for_create: true, clone_date: cloneDate} : {}),
        ...(entity === 'activities' && reassignmentTarget ? {activity_reassignment_target: reassignmentTarget} : {}),
        include: [
          "relations"
        ]
      }, id
    });
  }

  public getShowEntity(entity: string, id: number, related_entity?: string | null): Observable<Response<Entity>> {
    return this.http.post<Response<Entity>>(environment.baseApi + 'entity/show', {
      ...((related_entity && related_entity !== 'general') && {related_entity}),
      entity,
      id,
      data: [],
    });
  }

  /**
   * Raw DB columns for a record (rules: id + entity + field). Columns need not exist on any form.
   * repeater_children: child row ids keyed by alias (field name in conditions).
   * Identical concurrent requests (same entity, id, fields, repeaters) share one HTTP call — see recordAttributesKey.
   */
  public getRecordAttributes(
    entity: string,
    id: number,
    fields: string[],
    repeaterChildren: RepeaterRecordLookup[] = []
  ): Observable<Response<Record<string, unknown>>> {
    const key = EntityService.recordAttributesKey(entity, id, fields, repeaterChildren);
    const existing = this.recordAttributesShared.get(key);
    if (existing) {
      return existing;
    }
    const shared$ = this.http
      .post<Response<Record<string, unknown>>>(environment.baseApi + 'entity/record-attributes', {
        entity,
        id,
        fields,
        repeater_children: repeaterChildren,
      })
      .pipe(
        shareReplay({bufferSize: 1, refCount: true}),
        finalize(() => {
          this.recordAttributesShared.delete(key);
        })
      );
    this.recordAttributesShared.set(key, shared$);
    return shared$;
  }

  private static recordAttributesKey(
    entity: string,
    id: number,
    fields: string[],
    repeaterChildren: RepeaterRecordLookup[]
  ): string {
    const sorted = [...new Set(fields)].sort();
    const rep = [...repeaterChildren]
      .map(r => JSON.stringify([r.alias, r.child_entity, r.foreign_key, r.where ?? [], r.exists_only === true]))
      .sort()
      .join('|');
    return `${entity}:${id}:${sorted.join(',')}:rep:${rep}`;
  }

  public search(
    entity: string | null,
    related_entity: string,
    field: string,
    search_text: string | null,
    target: string,
    page: number,
    record_id: number | null,
    parentRecordIds: any,
    defaultFilters?: { relation?: string; conditions: unknown[] } | null
  ): Observable<any> {
    // Pass depends_from structure as-is (can be array of objects keyed by field name)
    const dependsFrom = parentRecordIds ?? null;
    const hasDefaultFilters = defaultFilters && Array.isArray((defaultFilters as { conditions?: unknown[] }).conditions)
      && (defaultFilters as { conditions: unknown[] }).conditions.length > 0;

    return this.http.post(environment.baseApi + 'entity/search', {
      entity,
      related_entity,
      ...(dependsFrom && {depends_from: dependsFrom}),
      data: {
        field,
        search_text,
        target,
        page,
        ...(record_id && {record_id}),
        ...(hasDefaultFilters && {default_filters: defaultFilters}),
      }
    });
  }

  public getOptions(config: OptionsConfig): Observable<Response<OptionsResponse>> {
    const {fieldId, page, relation_id, related_entity, stateId, operations} = config

    return this.http.post<Response<OptionsResponse>>(environment.baseApi + `field/${fieldId}/operations`, {
      page,
      per_page: TABLE__DEFAULT_PER_PAGE,
      ...(relation_id && {relation_id}),
      related_entity: EntityService.relatedEntityBody(related_entity),
      state_id: stateId,
      operations: operations,
    });
  }

  public searchInFilter(config: OptionsConfig, text: string): Observable<any> {
    const {fieldId, page, relation_id, related_entity, stateId, operations} = config

    return this.http.post(environment.baseApi + `field/${fieldId}/search`,
      {
        page,
        per_page: TABLE__DEFAULT_PER_PAGE,
        text,
        state_id: stateId,
        operations: operations ?? [],
        ...(relation_id && {relation_id}),
        related_entity: EntityService.relatedEntityBody(related_entity),
      },
    );
  }

  public getColors(): Observable<any> {
    return this.http.get(environment.baseApi + 'fields/colors');
  }

  public getCountries(): Observable<Response<Array<PhoneCountry>>> {
    return this.http
      .get<Response<{ locations: Array<PhoneCountry> }>>(environment.baseApi + 'locations')
      .pipe(
        map(response =>
          ({...response, data: [...response.data.locations]})
        ),
      )
  }

  public getBulkFields(entity: string): Observable<any> {
    return this.http.get(environment.baseApi + `field/updatable/entity/${entity}`);
  }

  public editBulkFields(data: FormData): Observable<any> {
    if (data.get('entity') === 'activities') return this.bulkEmailSave.save(data);
    return this.http.post<Response<{message?: string; bulk_payment_date?: unknown}>>(
      environment.baseApi + 'entity/bulk-update', data,
    ).pipe(tap(response => {
      if (response.data?.bulk_payment_date && typeof response.data.message === 'string') {
        this.bulkSaveToast.showMessage(response.data.message, 12000);
      }
    }));
  }

  public bulkSoftDelete(entity: string, recordsIds: number[]): Observable<Response<{ message: string }>> {
    return this.http.post<Response<{ message: string }>>(environment.baseApi + 'entity/bulk-delete', {
      entity,
      records_ids: recordsIds,
    });
  }

  public getScreenStates(id: number, page: number): Observable<any> {
    return this.http.post(environment.baseApi + `entity/${id}/states?page=${page}`, {});
  }

  public deleteScreenState(id: number, stateId: number): Observable<any> {
    return this.http.delete(environment.baseApi + `entity/${id}/state/${stateId}`);
  }

  public searchScreenState(id: number, text: string): Observable<any> {
    return this.http.post(environment.baseApi + `entity/${id}/state/search`, {text});
  }

  public selectScreenState(entityId: number, stateId: number, related_entity: any): Observable<any> {
    return this.http.put(environment.baseApi + `entity/${entityId}/state/select/by/${stateId}`, {
      ...(related_entity && {relation_id: related_entity.id})
    });
  }

  public updateScreenState(query: ScreenStateResponse, entityId: number): Observable<any> {
    return this.http.put(environment.baseApi + `entity/${entityId}/state/${query.id}`, query);
  }

  public createScreenState(query: ScreenStateResponse, entityId: number): Observable<Response<Category>> {
    return this.http.post<Response<Category>>(environment.baseApi + `entity/${entityId}/state`, query);
  }

  public saveScreenState(query: ScreenStateResponse, entityId: number): Observable<Response<Category>> {
    return query.id ? this.updateScreenState(query, entityId) : this.createScreenState(query, entityId);
  }

  public getDocuments(entityId: number | string): Observable<Response<EntityDocuments>> {
    return this.http.get<Response<{
      documents: Category[]
    }>>(environment.baseApiExternal + `entity/${entityId}/documents`);
  }

  public downloadDocument(id: number, entityId: number): Observable<Response<{ file: string, extension: string }>> {
    return this.http.get<Response<{
      file: string,
      extension: string
    }>>(`${this.baseUrl}document/${id}/record/${entityId}/generate`);
  }

  public downloadDocumentsBulk(id: number, entityIds: number[]): Observable<Response<{ file: string, extension?: string }>> {
    return this.http.post<Response<{ file: string, extension?: string }>>(`${this.baseUrl}document/${id}/bulk-generate`, {ids: entityIds})
  }

  public fileTemporaryPath(entity: string, id: string, field: string, fileIndex = 0): Observable<any> {
    return this.http.get(environment.baseApi + `inspect/path/entity/${entity}/id/${id}/field/${field}`, {
      params: {file_index: fileIndex},
    });
  }

  public fileStreamUrl(entity: string, id: string, field: string, fileIndex = 0): string {
    return environment.baseApi + `inspect/file/entity/${entity}/id/${id}/field/${field}?file_index=${fileIndex}`;
  }

  public fetchStoredFile(entity: string, id: string, field: string, fileIndex = 0): Observable<HttpResponse<Blob>> {
    return this.http.get(
      this.fileStreamUrl(entity, id, field, fileIndex),
      {
        observe: 'response',
        responseType: 'blob',
      }
    );
  }

  public runEntityAction(
    actionId: number,
    file: File,
    parentContext?: { entity: string; recordId: number | null },
  ): Observable<Response<{
    processed: number;
    updated: number;
    skipped: number;
    duplicates?: number;
    summary?: {
      title: string;
      message: string;
      problems: {
        code: string;
        count: number;
        message: string;
        action: string;
      }[];
    };
    updated_records?: {
      row: number;
      id: number;
      tax_code?: string;
      shipper?: string;
      invoice_number?: string | null;
      adjusted_final_amount?: number | string | null;
    }[];
    errors: {
      row: number;
      code?: string;
      reason: string;
      message?: string;
      action?: string;
      tax_code?: string;
      shipper?: string;
      ids?: number[];
    }[];
  }>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (parentContext?.recordId != null) {
      formData.append('parent_entity', parentContext.entity);
      formData.append('record_id', String(parentContext.recordId));
    }

    return this.http.post<Response<{
      processed: number;
      updated: number;
      skipped: number;
      duplicates?: number;
      summary?: {
        title: string;
        message: string;
        problems: {
          code: string;
          count: number;
          message: string;
          action: string;
        }[];
      };
      updated_records?: {
        row: number;
        id: number;
        tax_code?: string;
        shipper?: string;
        invoice_number?: string | null;
        adjusted_final_amount?: number | string | null;
      }[];
      errors: {
        row: number;
        code?: string;
        reason: string;
        message?: string;
        action?: string;
        tax_code?: string;
        shipper?: string;
        ids?: number[];
      }[];
    }>>(`${this.baseUrl}actions/${actionId}/run`, formData);
  }

  public runRecordEntityAction(
    actionId: number,
    recordId: number,
  ): Observable<Response<{
    file: string;
    extension: string;
    filename?: string;
  }>> {
    return this.http.post<Response<{
      file: string;
      extension: string;
      filename?: string;
    }>>(`${this.baseUrl}actions/${actionId}/run`, {record_id: recordId});
  }
}
